<!-- #profile Events tab: the events the user attended or is registered for, under Upcoming
     Events (not ended yet, soonest first) and Previous Events (newest first). There's no
     unregistering (decided 2026-10-08); a VIZBI Online event they paid for links to its paid
     invoice on Stripe (Invoice/Receipt, in a new tab); this year's others say Paid or Free.
     Then REGISTRATIONS YOU PAID FOR: what they paid for others (publicMetadata.purchases, written
     by the payment webhook; fullstack ROADMAP.md § Registering on behalf of others), each with
     whom it's for and its invoice. -->
<template>
  <div class="section">
    <p v-if="!attendedEvents.length && !purchases.length" class="empty">No events yet.</p>
    <template v-for="group in groups" :key="group.title">
      <div v-if="group.items.length" class="title">{{ group.title }}</div>
      <div v-if="group.items.length" class="list">
        <div v-for="item in group.items" :key="item.key" class="card">
          <div class="heading">
            <!-- the delivery beside the name, its weight normal (Seán, 2026-10-23) -->
            <span class="name">
              {{ item.title }}<span class="delivery"> · {{ item.deliveryLabel }}</span>
            </span>
            <a
              v-if="item.receiptUrl"
              class="receipt"
              :href="item.receiptUrl"
              target="_blank"
              rel="noopener">
              Invoice/Receipt
            </a>
            <span v-else-if="item.paymentLabel" class="paymentLabel">{{ item.paymentLabel }}</span>
          </div>
          <span class="meta">{{ item.dateText }}</span>
        </div>
      </div>
    </template>
    <template v-if="purchases.length">
      <div class="title">REGISTRATIONS YOU PAID FOR</div>
      <div class="list">
        <div v-for="item in purchases" :key="item.key" class="card">
          <div class="heading">
            <span class="name">{{ item.title }}</span>
            <a
              v-if="item.receiptUrl"
              class="receipt"
              :href="item.receiptUrl"
              target="_blank"
              rel="noopener">
              Invoice/Receipt
            </a>
          </div>
          <span class="meta">For {{ item.participants }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import {computed} from "vue";
import useAttendedEvents from "@/composables/useAttendedEvents";
import useUserSafe from "@/composables/useUserSafe";

// "masterclass-2026" → "Masterclass"; the year once, after them all
function getPurchaseTitle(eventKeys) {
  const year = String(eventKeys[0] || "").match(/\d{4}$/)?.[0] || "";
  const names = eventKeys.map((key) => {
    const words = key.replace(/-\d{4}$/, "").split("-");
    return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  });
  return `VIZBI Online ${names.join(" and ")} ${year}`.trim();
}

function getPurchaseItem(purchase) {
  const participants = (purchase.participants || []).join(" and ");
  const title = getPurchaseTitle(purchase.events || []);
  return {key: purchase.invoiceId, title, participants, receiptUrl: purchase.hostedUrl || ""};
}

const {user} = useUserSafe();
// newest first
const purchases = computed(() => {
  const list = user.value?.publicMetadata?.purchases || [];
  return [...list].sort((first, second) => second.at - first.at).map(getPurchaseItem);
});
const {attendedEvents, upcomingEvents, previousEvents} = useAttendedEvents();
const groups = computed(() => [
  {title: "UPCOMING EVENTS", items: upcomingEvents.value},
  {title: "PREVIOUS EVENTS", items: previousEvents.value}
]);
</script>

<style scoped>
.section {
  margin-top: 8px;
}
.title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: rgba(0, 0, 0, 0.35);
  margin: 6px 0 5px 0;
}
/* Previous Events, after the upcoming ones: space above its heading */
.list + .title {
  margin-top: 20px;
}
.empty {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
}
.list {
  display: grid;
  gap: 8px;
}
/* the same card as the Settings tab's sections (Videos.vue) */
.card {
  display: grid;
  gap: 2px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  text-align: left;
}
/* the event's name, and its Invoice/Receipt link on the same line, right-aligned */
.heading {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}
/* whole-pixel line heights: with the font's own (fractional) one, the cards rounded to heights a
   pixel apart */
.name,
.meta,
.receipt,
.paymentLabel {
  line-height: 18px;
}
.name {
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.75);
}
.delivery {
  font-weight: 400;
}
.meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}
/* a text link in the brand colour, underlined as links are */
/* Paid or Free where there's no link: the link's place and size, a light grey, not selectable */
.paymentLabel {
  font-size: 12px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.35);
  user-select: none;
}
.receipt {
  font-size: 12px;
  font-weight: 700;
  color: var(--brand-color);
  text-decoration: underline;
  white-space: nowrap;
}
</style>
